import React, { useEffect, useLayoutEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import brandIcon from "./assets/brand/Inverlux-Icon_512.png";
import tinyIcon from "./assets/tiny-jpg-png/icon-128.png";
import tabGroupShareIcon from "./assets/tab-group-share/icon128.png";
import collapseAllIcon from "./assets/gmail-collapse-all/icon-source.svg";
import tinySocialImage from "./assets/tiny-jpg-png/web/social-banner.webp";
import styles from "./InverluxApps.module.css";

const pageMetadata = {
  "/": {
    title: "Inverlux Apps | Focused browser utilities",
    description:
      "Small, focused browser utilities developed by Inverlux LLC.",
  },
  "/tiny-jpg-png": {
    title: "Tiny JPG & PNG | No Uploads, No Subscriptions | Inverlux Apps",
    description:
      "No uploads, no subscriptions. Compress JPG and PNG images entirely on your device in Chrome, in Free and Pro. Start free or upgrade once.",
    image: tinySocialImage,
    imageAlt: "The Tiny JPG & PNG bear holding a salmon beside image files becoming smaller.",
  },
  "/tiny-jpg-png/privacy": {
    title: "Privacy Policy | Tiny JPG & PNG",
    description:
      "Privacy policy for Tiny JPG & PNG by Inverlux LLC: local image processing, downloads, and optional Pro checkout, licensing, retention, and deletion requests.",
  },
  "/gmail-collapse-all": {
    title: "Collapse All for Gmail | Inverlux Apps",
    description:
      "A free Chrome extension that keeps a convenient Collapse all control available in Gmail conversation view.",
  },
  "/gmail-collapse-all/privacy": {
    title: "Privacy Policy | Collapse All for Gmail",
    description:
      "Privacy policy for the Collapse All for Gmail Chrome extension from Inverlux LLC.",
  },
  "/tab-group-share": {
    title: "Tab Group Share | Inverlux Apps",
    description:
      "Copy a Chrome tab group's titles and URLs into AI prompts, notes, spreadsheets, and email. Export as Plain Text, Markdown, or JSON, and preview imports locally.",
  },
  "/tab-group-share/privacy": {
    title: "Privacy Policy | Tab Group Share",
    description:
      "Privacy policy for Tab Group Share, a local tab-group export and import Chrome extension by Inverlux LLC.",
  },
  "/support": {
    title: "Support | Inverlux Apps",
    description: "Support for browser utilities developed by Inverlux LLC.",
  },
};

const AppsLayout = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    if (!location.hash) {
      const resetScroll = () => window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      resetScroll();
      // A pending smooth anchor scroll can advance once after the route changes.
      const frame = window.requestAnimationFrame(resetScroll);
      return () => window.cancelAnimationFrame(frame);
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const pathname = location.pathname.replace(/\/+$/, "") || "/";
    const metadata = pageMetadata[pathname] || pageMetadata["/"];
    const description = document.querySelector('meta[name="description"]');
    const previousTitle = document.title;
    const previousDescription = description
      ? description.getAttribute("content")
      : null;
    const restoreHead = [];

    document.title = metadata.title;
    if (description) {
      description.setAttribute("content", metadata.description);
    }

    if (metadata.image) {
      const url = "https://apps.inverlux.com" + pathname;
      const imageUrl = new URL(metadata.image, "https://apps.inverlux.com").href;
      const setHeadElement = (tag, key, value, attribute, content) => {
        const existing = document.querySelector(tag + "[" + key + '="' + value + '"]');
        const element = existing || document.createElement(tag);
        const previous = element.getAttribute(attribute);
        element.setAttribute(key, value);
        element.setAttribute(attribute, content);
        if (!existing) document.head.appendChild(element);
        restoreHead.push(() => {
          if (!existing) element.remove();
          else if (previous === null) element.removeAttribute(attribute);
          else element.setAttribute(attribute, previous);
        });
      };
      setHeadElement("link", "rel", "canonical", "href", url);
      [
        ["og:type", "website"],
        ["og:site_name", "Inverlux Apps"],
        ["og:title", metadata.title],
        ["og:description", metadata.description],
        ["og:url", url],
        ["og:image", imageUrl],
        ["og:image:alt", metadata.imageAlt],
        ["og:image:width", "1200"],
        ["og:image:height", "480"],
        ["og:image:type", "image/webp"],
      ].forEach(([property, content]) =>
        setHeadElement("meta", "property", property, "content", content)
      );
      [
        ["twitter:card", "summary_large_image"],
        ["twitter:title", metadata.title],
        ["twitter:description", metadata.description],
        ["twitter:image", imageUrl],
        ["twitter:image:alt", metadata.imageAlt],
      ].forEach(([name, content]) =>
        setHeadElement("meta", "name", name, "content", content)
      );
    }

    return () => {
      document.title = previousTitle;
      if (description && previousDescription !== null) {
        description.setAttribute("content", previousDescription);
      }
      restoreHead.forEach((restore) => restore());
    };
  }, [location.pathname]);

  return (
    <div className={styles.site}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link className={styles.brand} to="/" aria-label="Inverlux Apps home">
            <img className={styles.brandMark} src={brandIcon} alt="" />
            <span>
              <span className={styles.brandName}>Inverlux Apps</span>
              <span className={styles.brandCompany}>by Inverlux LLC</span>
            </span>
          </Link>
          <nav className={styles.navigation} aria-label="Apps navigation">
            <NavLink className={styles.appNavigationLink} to="/tiny-jpg-png">
              <img src={tinyIcon} alt="" width="22" height="22" />
              <span>Tiny JPG &amp; PNG</span>
            </NavLink>
            <NavLink className={styles.appNavigationLink} to="/tab-group-share">
              <img src={tabGroupShareIcon} alt="" width="22" height="22" />
              <span>Tab Group Share</span>
            </NavLink>
            <NavLink className={styles.appNavigationLink} to="/gmail-collapse-all">
              <img src={collapseAllIcon} alt="" width="22" height="22" />
              <span>Collapse All</span>
            </NavLink>
            <NavLink className={styles.supportNavigationLink} to="/support">Support</NavLink>
          </nav>
        </div>
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerTopline}>
            <span>Inverlux Apps</span>
            <span>Browser utilities developed by Inverlux LLC</span>
          </div>
          <p className={styles.disclaimer}>
            Gmail is a trademark of Google LLC. Collapse All for Gmail is
            independently developed by Inverlux LLC and is not affiliated with
            or endorsed by Google.
          </p>
          <div className={styles.footerLinks}>
            <Link to="/tiny-jpg-png/privacy">Tiny JPG &amp; PNG privacy</Link>
            <Link to="/gmail-collapse-all/privacy">Collapse All privacy</Link>
            <Link to="/tab-group-share/privacy">Tab Group Share privacy</Link>
            <Link to="/support">Support</Link>
            <a href="mailto:support@inverlux.com">support@inverlux.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AppsLayout;
